import { Link } from "@tanstack/react-router";
import { ArrowRight, CheckCircle, MapPin } from "lucide-react";

import { SectionHeading } from "./SectionHeading";
import { CtaBand } from "./CtaBand";
import { QuickQuote } from "./QuickQuote";
import { SITE_URL, ogImageDefault, ogImageMeta } from "../lib/seo";
import { kostenDisclaimer } from "../lib/kosten-content";
import type { DienstLocatieContent } from "../lib/dienst-locatie-content";

interface DienstLocatiePageProps {
  content: DienstLocatieContent;
}

export function DienstLocatiePage({ content }: DienstLocatiePageProps) {
  return (
    <>
      <div className="py-14 sm:py-20 lg:py-28">
        <div className="container-tight">
          <SectionHeading
            as="h1"
            eyebrow={content.eyebrow}
            title={content.h1}
            description={content.intro}
            align="center"
            className="mx-auto"
          />

          <div className="mx-auto mt-10 max-w-3xl space-y-14 sm:mt-14">
            <section className="rounded-2xl border border-border/60 bg-card p-6 sm:p-8">
              <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground">
                {content.prijsLabel}
              </h2>
              <p className="mt-3 font-heading text-3xl font-bold leading-tight tracking-tight text-primary sm:text-4xl">
                {content.prijs}
              </p>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {content.prijsNoot}
              </p>

              <ul className="mt-6 space-y-2.5 text-sm leading-relaxed text-foreground/80">
                {content.usps.map((usp) => (
                  <li key={usp} className="flex items-start gap-2.5">
                    <CheckCircle
                      className="mt-0.5 h-4 w-4 shrink-0 text-primary"
                      aria-hidden="true"
                    />
                    <span className="min-w-0">{usp}</span>
                  </li>
                ))}
              </ul>

              <p className="mt-5 text-xs leading-relaxed text-muted-foreground">
                {kostenDisclaimer}
              </p>

              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/contact"
                  className="inline-flex min-h-12 items-center justify-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
                >
                  Gratis offerte aanvragen
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
                <Link
                  to={content.kostenPad ?? "/kozijnen-kosten"}
                  className="inline-flex min-h-12 items-center justify-center gap-2 rounded-md border border-input bg-background px-6 py-3 text-sm font-semibold text-foreground transition-colors hover:bg-accent"
                >
                  Volledige kostengids
                </Link>
              </div>
            </section>

            <QuickQuote onderwerp={content.dienst.toLowerCase()} />


            <section className="space-y-6">
              {content.paragraphs.map((p) => (
                <p
                  key={p.slice(0, 24)}
                  className="text-base leading-relaxed text-muted-foreground"
                >
                  {p}
                </p>
              ))}
            </section>

            {content.wijken && content.wijken.length > 0 && (
              <section className="rounded-2xl border border-border/60 bg-card p-6 sm:p-8">
                <h2 className="font-heading text-xl font-semibold text-foreground">
                  {content.dienst} plaatsen in heel {content.plaats}
                </h2>
                <ul className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {content.wijken.map((wijk) => (
                    <li
                      key={wijk}
                      className="flex items-center gap-2 text-sm text-muted-foreground"
                    >
                      <MapPin className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                      {wijk}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section>
              <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground">
                Veelgestelde vragen over {content.dienst.toLowerCase()} in {content.plaats}
              </h2>
              <div className="mt-6 space-y-4">
                {content.faq.map((item) => (
                  <div
                    key={item.vraag}
                    className="rounded-xl border border-border/60 bg-card p-4 sm:p-5"
                  >
                    <h3 className="font-heading text-base font-semibold text-foreground">
                      {item.vraag}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                      {item.antwoord}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            <section className="flex flex-wrap gap-x-6 gap-y-2 text-sm font-semibold">
              <Link
                to="/diensten/$slug"
                params={{ slug: content.dienstSlug }}
                className="inline-flex items-center gap-1 text-primary hover:text-primary/80"
              >
                Meer over onze {content.dienst.toLowerCase()}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <Link
                to={content.locatiePad ?? "/timmerman-dordrecht"}
                className="inline-flex items-center gap-1 text-primary hover:text-primary/80"
              >
                Timmerman {content.plaats}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </section>
          </div>
        </div>
      </div>

      <CtaBand />
    </>
  );
}

export function dienstLocatieHead(content: DienstLocatieContent) {
  const path = `/${content.slug}`;
  return {
    meta: [
      { title: content.title },
      { name: "description", content: content.description },
      { property: "og:title", content: content.socialTitle },
      { name: "twitter:title", content: content.socialTitle },
      { property: "og:description", content: content.socialDescription },
      { name: "twitter:description", content: content.socialDescription },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE_URL}${path}` },
      ...ogImageMeta(ogImageDefault, `${content.dienst} ${content.plaats} — Fedor Timmerwerken`),
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}${path}` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: `${content.dienst} ${content.plaats}`,
          description: content.description,
          serviceType: content.dienst,
          url: `${SITE_URL}${path}`,
          areaServed: { "@type": "City", name: content.plaats },
          provider: { "@id": `${SITE_URL}/#business` },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: content.faq.map((item) => ({
            "@type": "Question",
            name: item.vraag,
            acceptedAnswer: { "@type": "Answer", text: item.antwoord },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Diensten", item: `${SITE_URL}/diensten` },
            {
              "@type": "ListItem",
              position: 3,
              name: `${content.dienst} ${content.plaats}`,
              item: `${SITE_URL}${path}`,
            },
          ],
        }),
      },
    ],
  };
}
